import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { updateUser, type User } from '@/api/users'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
interface Props {
  user: User
  onUpdated: (user: User) => void
}

export interface PersonalDataTabHandle {
  save: () => Promise<void>
}

const MEMBER_CATEGORIES: { value: 'client' | 'staff' | 'partner'; label: string }[] = [
  { value: 'client',  label: 'Miembro' },
  { value: 'staff',   label: 'Personal' },
  { value: 'partner', label: 'Socio' },
]

// The device only recognizes these three — any other value gets rejected
// with "badJsonContent:userType" every time we try to sync the user.
const USER_TYPES = [
  { value: 'normal',    label: 'Miembro regular' },
  { value: 'visitor',   label: 'Visitante' },
  { value: 'blackList', label: 'Lista restringida' },
]

const BLOOD_TYPES = ['A+', 'A−', 'B+', 'B−', 'AB+', 'AB−', 'O+', 'O−']
const GOALS = [
  'Bajar de peso',
  'Ganar músculo',
  'Mejorar condición física',
  'Rehabilitación / Lesión',
  'Rendimiento deportivo',
  'Mantenimiento',
  'Otro',
]

function calcAge(birth: string): number | null {
  if (!birth) return null
  const b = new Date(birth + 'T12:00:00')
  if (isNaN(b.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - b.getFullYear()
  const m = today.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--
  return age
}

const inputCls = 'w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent'
const labelCls = 'text-text-muted text-xs font-medium uppercase tracking-wide mb-1 block'

export const PersonalDataTab = forwardRef<PersonalDataTabHandle, Props>(function PersonalDataTab({ user, onUpdated }, ref) {
  const [name, setName]                             = useState(user.name)
  const [documentType, setDocumentType]             = useState(user.document_type ?? '')
  const [idNumber, setIdNumber]                     = useState(user.id_number ?? '')
  const [email, setEmail]                           = useState(user.email ?? '')
  const [phone, setPhone]                           = useState(user.phone ?? '')
  const [birthDate, setBirthDate]                   = useState(user.birth_date?.slice(0, 10) ?? '')
  const [bloodType, setBloodType]                   = useState(user.blood_type ?? '')
  const [gender, setGender]                         = useState(user.gender ?? '')
  const [address, setAddress]                       = useState(user.address ?? '')
  const [occupation, setOccupation]                 = useState(user.occupation ?? '')
  const [emergencyName, setEmergencyName]           = useState(user.emergency_contact_name ?? '')
  const [emergencyPhone, setEmergencyPhone]         = useState(user.emergency_contact_phone ?? '')
  const [goal, setGoal]                             = useState(user.goal ?? '')
  const [medicalNotes, setMedicalNotes]             = useState(user.medical_notes ?? '')
  const [memberCategory, setMemberCategory]         = useState<'client' | 'staff' | 'partner'>(user.member_category ?? 'client')
  const [userType, setUserType]                     = useState(user.user_type ?? 'normal')
  const [saving, setSaving]                         = useState(false)

  useEffect(() => {
    setName(user.name)
    setDocumentType(user.document_type ?? '')
    setIdNumber(user.id_number ?? '')
    setEmail(user.email ?? '')
    setPhone(user.phone ?? '')
    setBirthDate(user.birth_date?.slice(0, 10) ?? '')
    setBloodType(user.blood_type ?? '')
    setGender(user.gender ?? '')
    setAddress(user.address ?? '')
    setOccupation(user.occupation ?? '')
    setEmergencyName(user.emergency_contact_name ?? '')
    setEmergencyPhone(user.emergency_contact_phone ?? '')
    setGoal(user.goal ?? '')
    setMedicalNotes(user.medical_notes ?? '')
    setMemberCategory(user.member_category ?? 'client')
    setUserType(user.user_type ?? 'normal')
  }, [user.id])

  const age = calcAge(birthDate)

  useImperativeHandle(ref, () => ({ save: handleSave }))

  async function handleSave() {
    setSaving(true)
    try {
      // beginTime/endTime are intentionally not sent from here — the real
      // access window comes from the person's active membership (or from
      // the unlimited-access rule for staff/partner), never from a manual
      // date picked on this tab. Leaving them out lets the backend's
      // COALESCE preserve whatever is already correct.
      const r = await updateUser(user.id, {
        name,
        memberCategory,
        userType,
        longTerm: memberCategory !== 'client',
        documentType: documentType || undefined,
        idNumber: idNumber || undefined,
        email: email || undefined,
        phone: phone || undefined,
        birthDate: birthDate || undefined,
        bloodType: bloodType || undefined,
        gender: gender || undefined,
        address: address || undefined,
        occupation: occupation || undefined,
        emergencyContactName: emergencyName || undefined,
        emergencyContactPhone: emergencyPhone || undefined,
        goal: goal || undefined,
        medicalNotes: medicalNotes || undefined,
      })
      onUpdated(r.user)
      toast.success('Datos actualizados')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">

      {/* ── Categoría y acceso ── */}
      {/* Validez (fechas) ya no se edita aquí: sale automáticamente de la
          categoría (personal/socio = ilimitado) o de la membresía activa
          (miembro). Se cambia muy pocas veces por persona. */}
      <Section title="Categoría y acceso"
        hint={MEMBER_CATEGORIES.find(c => c.value === memberCategory)?.label}>
        <div>
          <p className={labelCls}>Categoría</p>
          <div className="flex flex-wrap gap-2">
            {MEMBER_CATEGORIES.map(c => (
              <button key={c.value} type="button"
                onClick={() => setMemberCategory(c.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${memberCategory === c.value ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary'}`}>
                {c.label}
              </button>
            ))}
          </div>
          {memberCategory !== 'client' ? (
            <p className="text-text-muted text-xs mt-1.5">
              {memberCategory === 'staff' ? 'Personal del establecimiento' : 'Socio'} — acceso ilimitado, sin depender de una membresía.
            </p>
          ) : (
            <p className="text-text-muted text-xs mt-1.5">
              La validez del acceso se toma de la membresía activa.
            </p>
          )}
        </div>
        <AdvancedOptions>
          <div>
            <label className={labelCls}>
              Tipo <span className="normal-case text-text-muted/70">(del dispositivo — casi siempre "Miembro regular")</span>
            </label>
            <select value={userType} onChange={e => setUserType(e.target.value)} className={inputCls}>
              {USER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </AdvancedOptions>
      </Section>

      {/* ── Identificación ── */}
      <Section title="Identificación" defaultOpen>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={labelCls}>Nombre completo *</label>
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Cédula / Documento de identidad</label>
            <div className="flex gap-2">
              <select
                className="shrink-0 w-24 bg-background border border-border rounded-md px-2 py-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                value={documentType}
                onChange={e => setDocumentType(e.target.value)}
              >
                <option value="">Tipo</option>
                <option value="CC">CC</option>
                <option value="CE">CE</option>
                <option value="TI">TI</option>
                <option value="PA">PA</option>
                <option value="NIT">NIT</option>
                <option value="RC">RC</option>
                <option value="Otro">Otro</option>
              </select>
              <input
                className="flex-1 min-w-0 bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                value={idNumber}
                onChange={e => setIdNumber(e.target.value)}
                placeholder="Número de cédula"
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>
              Fecha de nacimiento
              {age !== null && <span className="ml-2 text-accent font-semibold normal-case">{age} años</span>}
            </label>
            <input type="date" className={inputCls} value={birthDate} onChange={e => setBirthDate(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Género</label>
            <select className={inputCls} value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">Sin especificar</option>
              <option value="male">Masculino</option>
              <option value="female">Femenino</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Tipo de sangre</label>
            <select className={inputCls} value={bloodType} onChange={e => setBloodType(e.target.value)}>
              <option value="">Sin especificar</option>
              {BLOOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </Section>

      {/* ── Contacto ── */}
      <Section title="Contacto"
        defaultOpen={!!(phone || email || address || occupation)}
        hint={phone || email || 'Sin datos'}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Teléfono / Celular</label>
            <input className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 3001234567" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@ejemplo.com" />
          </div>
          <div>
            <label className={labelCls}>Ocupación / Profesión</label>
            <input className={inputCls} value={occupation} onChange={e => setOccupation(e.target.value)} placeholder="Ej: Estudiante, Contador..." />
          </div>
          <div>
            <label className={labelCls}>Dirección</label>
            <input className={inputCls} value={address} onChange={e => setAddress(e.target.value)} placeholder="Barrio, ciudad..." />
          </div>
        </div>
      </Section>

      {/* ── Contacto de emergencia ── */}
      <Section title="Contacto de emergencia"
        defaultOpen={!!(emergencyName || emergencyPhone)}
        hint={emergencyName || 'Sin registrar'}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Nombre</label>
            <input className={inputCls} value={emergencyName} onChange={e => setEmergencyName(e.target.value)} placeholder="Nombre y apellido" />
          </div>
          <div>
            <label className={labelCls}>Teléfono</label>
            <input className={inputCls} value={emergencyPhone} onChange={e => setEmergencyPhone(e.target.value)} placeholder="Ej: 3009876543" />
          </div>
        </div>
      </Section>

      {/* ── Salud y objetivo ── */}
      <Section title="Salud y objetivo"
        defaultOpen={!!(goal || medicalNotes)}
        hint={goal || 'Sin registrar'}>
        <div className="flex flex-col gap-3">
          <div>
            <label className={labelCls}>Objetivo en el gimnasio</label>
            <div className="flex flex-wrap gap-2">
              {GOALS.map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(goal === g ? '' : g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    goal === g
                      ? 'bg-accent text-background border-accent'
                      : 'bg-transparent text-text-muted border-border hover:text-text-primary hover:border-accent/40'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Observaciones médicas / Condiciones</label>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              value={medicalNotes}
              onChange={e => setMedicalNotes(e.target.value)}
              placeholder="Lesiones, enfermedades, medicamentos, contraindicaciones..."
            />
          </div>
        </div>
      </Section>

    </div>
  )
})

function AdvancedOptions({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-1 text-text-muted text-xs font-medium uppercase tracking-wide hover:text-text-primary transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Opciones avanzadas
      </button>
      {open && <div className="mt-2 flex flex-col gap-3">{children}</div>}
    </div>
  )
}

function Section({ title, hint, defaultOpen = false, children }: {
  title: string
  hint?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-surface hover:bg-background transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-text-muted" /> : <ChevronRight className="w-3.5 h-3.5 text-text-muted" />}
          <span className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{title}</span>
        </div>
        {!open && hint && (
          <span className="text-xs text-text-muted truncate max-w-[180px]">{hint}</span>
        )}
      </button>
      {open && (
        <div ref={contentRef} className="px-4 pb-4 pt-3 flex flex-col gap-3 bg-background/40">
          {children}
        </div>
      )}
    </div>
  )
}
